import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { useReactFlow } from 'reactflow';
import { AppNode, Edge, ProcessDefinition, Swimlane, NamedRuleSet } from '../types';
import { getNodeTypes, getDefaultData } from '../utils/nodeRegistry';
import { validateDiagram } from '../utils/validation';
import {
  createWorkflowAiCapability,
  EditorAiCapability,
  WorkflowModelAccess,
  ViewAccess,
} from '../utils/aiCapability';

interface UseAiCapabilityArgs {
  nodes: AppNode[];
  edges: Edge[];
  setNodes: Dispatch<SetStateAction<AppNode[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  processes: ProcessDefinition[];
  activeProcessId: string;
  swimlanes: Swimlane[];
  ruleSets: NamedRuleSet[];
  /** Current selection / modal snapshot for get_ui_state. */
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  selectedSwimlaneId: string | null;
  modalOpen: boolean;
  modalNodeId: string | null;
  modalTabId: string | null;
  /** Real editor view/selection/process operations the view tools route through. */
  fitView: (opts?: { nodes?: { id: string }[]; padding?: number; duration?: number; minZoom?: number; maxZoom?: number }) => void;
  focusNode: (nodeId: string) => void;
  focusAndSelectNode: (nodeId: string) => void;
  selectNode: (nodeId: string) => void;
  selectEdge: (edgeId: string) => void;
  selectSwimlane: (swimlaneId: string | null) => void;
  clearSelection: () => void;
  openModalAtTab: (nodeId: string, tabId?: string) => void;
  switchProcess: (processId: string) => void;
  onAiCapabilityReady?: (cap: EditorAiCapability | null) => void;
}

/**
 * Wires an {@link EditorAiCapability} to the live editor model and hands it to
 * the host via `onAiCapabilityReady`. The capability's tools route through the
 * editor's REAL mutation (setNodes/setEdges) and validation (validateDiagram)
 * so bad input is rejected with a reason rather than corrupting the model.
 *
 * State is read through refs so a single, stable capability object always sees
 * the latest nodes/edges — we therefore call `onAiCapabilityReady(cap)` exactly
 * once (on mount / when the callback identity changes), not on every render.
 */
export const useAiCapability = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  processes,
  activeProcessId,
  swimlanes,
  ruleSets,
  selectedNodeId,
  selectedEdgeId,
  selectedSwimlaneId,
  modalOpen,
  modalNodeId,
  modalTabId,
  fitView,
  focusNode,
  focusAndSelectNode,
  selectNode,
  selectEdge,
  selectSwimlane,
  clearSelection,
  openModalAtTab,
  switchProcess,
  onAiCapabilityReady,
}: UseAiCapabilityArgs): void => {
  // Latest-state refs so the stable capability sees fresh data on each invoke.
  const stateRef = useRef({
    nodes,
    edges,
    processes,
    activeProcessId,
    swimlanes,
    ruleSets,
    selectedNodeId,
    selectedEdgeId,
    selectedSwimlaneId,
    modalOpen,
    modalNodeId,
    modalTabId,
  });
  stateRef.current = {
    nodes,
    edges,
    processes,
    activeProcessId,
    swimlanes,
    ruleSets,
    selectedNodeId,
    selectedEdgeId,
    selectedSwimlaneId,
    modalOpen,
    modalNodeId,
    modalTabId,
  };

  // Editor view/selection/process operations through refs so the stable
  // capability always invokes the latest closures.
  const opsRef = useRef({ fitView, focusNode, focusAndSelectNode, selectNode, selectEdge, selectSwimlane, clearSelection, openModalAtTab, switchProcess });
  opsRef.current = { fitView, focusNode, focusAndSelectNode, selectNode, selectEdge, selectSwimlane, clearSelection, openModalAtTab, switchProcess };

  // React Flow instance for the low-level viewport primitive (set_viewport).
  const reactFlow = useReactFlow();
  const reactFlowRef = useRef(reactFlow);
  reactFlowRef.current = reactFlow;

  // Hold the host callback in a ref so the publish effect does NOT depend on it
  // directly. An inline host callback (new identity every host render) would
  // otherwise re-fire the unregister/register cycle on every render.
  const onReadyRef = useRef(onAiCapabilityReady);
  onReadyRef.current = onAiCapabilityReady;

  // Stable model-access bound to refs + real mutators. Built once.
  const capability = useMemo<EditorAiCapability>(() => {
    const view: ViewAccess = {
      getSwimlanes: () => stateRef.current.swimlanes,
      getProcesses: () =>
        stateRef.current.processes.map((p) => ({ id: p.id, name: p.name, active: p.id === stateRef.current.activeProcessId })),
      getViewport: () => {
        const vp = reactFlowRef.current.getViewport();
        return { x: vp.x, y: vp.y, zoom: vp.zoom };
      },
      getSelection: () => {
        const s = stateRef.current;
        if (s.selectedNodeId) return { kind: 'node', id: s.selectedNodeId };
        if (s.selectedEdgeId) return { kind: 'edge', id: s.selectedEdgeId };
        if (s.selectedSwimlaneId) return { kind: 'swimlane', id: s.selectedSwimlaneId };
        return { kind: 'none', id: null };
      },
      getModalState: () => {
        const s = stateRef.current;
        return { open: s.modalOpen, nodeId: s.modalNodeId, tabId: s.modalTabId };
      },
      setViewport: (v, durationMs) =>
        reactFlowRef.current.setViewport({ x: v.x, y: v.y, zoom: v.zoom }, durationMs ? { duration: durationMs } : undefined),
      fitView: (opts) => {
        const nodeArg = opts.nodeIds && opts.nodeIds.length > 0 ? opts.nodeIds.map((id) => ({ id })) : undefined;
        opsRef.current.fitView({
          nodes: nodeArg,
          padding: opts.padding,
          duration: opts.durationMs ?? 600,
          minZoom: opts.minZoom,
          maxZoom: opts.maxZoom,
        });
      },
      centerNode: (nodeId, zoom, durationMs) => {
        const node = stateRef.current.nodes.find((n) => n.id === nodeId);
        if (!node) return;
        reactFlowRef.current.setCenter(node.position.x + 90, node.position.y + 32, {
          zoom: zoom ?? 1.2,
          duration: durationMs ?? 800,
        });
      },
      selectNode: (nodeId) => opsRef.current.focusAndSelectNode(nodeId),
      selectEdge: (edgeId) => opsRef.current.selectEdge(edgeId),
      selectSwimlane: (swimlaneId) => opsRef.current.selectSwimlane(swimlaneId),
      clearSelection: () => opsRef.current.clearSelection(),
      openNodeEditor: (nodeId, tabId) => opsRef.current.openModalAtTab(nodeId, tabId),
      switchProcess: (processId) => opsRef.current.switchProcess(processId),
    };
    const access: WorkflowModelAccess = {
      getNodes: () => stateRef.current.nodes,
      getEdges: () => stateRef.current.edges,
      getNodeTypes: () => getNodeTypes(),
      getDefaultData: (type) => getDefaultData(type),
      applyNodes: (updater) => setNodes(updater),
      applyEdges: (updater) => setEdges(updater),
      // Validate a CANDIDATE diagram: splice the supplied nodes/edges into the
      // active process and run the editor's REAL validateDiagram over the whole
      // project (so cross-process + ruleset rules still fire). Other process
      // state (swimlanes) is read from the latest refs.
      validate: (candidateNodes, candidateEdges) => {
        const s = stateRef.current;
        const merged = s.processes.map((p) =>
          p.id === s.activeProcessId
            ? { ...p, nodes: candidateNodes, edges: candidateEdges, swimlanes: s.swimlanes }
            : p
        );
        return validateDiagram(merged, s.ruleSets);
      },
      view,
    };
    return createWorkflowAiCapability(access);
    // setNodes/setEdges from useNodesState/useEdgesState are stable.
  }, [setNodes, setEdges]);

  // Publish once when the (stable) capability is created; retract the SAME
  // capability on unmount. Reads the host callback through a ref so a changing
  // inline callback identity does not re-fire this effect every render.
  useEffect(() => {
    onReadyRef.current?.(capability);
    return () => onReadyRef.current?.(null);
  }, [capability]);
};
